import { fn } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismComboSelectInput,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type MarketParityParityManagerController from 'lumen/controllers/market/parity/parity-manager';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';
import type {
  ParityManagerFiltersAssignee,
  ParityManagerFiltersRootCause,
  ParityManagerFiltersStatus,
} from 'lumen/models';

import styles from './add-issue-form.module.css';

interface AssigneeOption {
  assignee: ParityManagerFiltersAssignee;
  label: string;
}

interface RootCauseOption {
  label: string;
  rootCause: ParityManagerFiltersRootCause;
}

interface StatusOption {
  label: string;
  status: ParityManagerFiltersStatus;
}

interface ParityParityManagerAdditionalInfoFormSignature {
  Args: {
    back: () => void;
    issue: NewIssue;
    next: () => void;
    parentController: MarketParityParityManagerController;
    setIssueProperty: (property: string, newValue: unknown) => void;
    setIssuePropertyFromEvent: (property: string, event: Event) => void;
  };
}

export default class ParityParityManagerAdditionalInfoForm extends Component<ParityParityManagerAdditionalInfoFormSignature> {
  @cached
  get possibleAssigneeOptions(): AssigneeOption[] {
    return this.args.parentController.possibleAssignees.map((assignee) => ({
      label: assignee.name,
      assignee,
    }));
  }

  get possibleAssigneeValue(): AssigneeOption | undefined {
    return this.possibleAssigneeOptions.find(
      (option) => option.assignee === this.args.issue.assignee,
    );
  }

  @cached
  get possibleRootcausesWithoutDefaultOptions(): RootCauseOption[] {
    return this.args.parentController.possibleRootcausesWithoutDefault.map(
      (rootCause) => ({
        label: rootCause.name,
        rootCause,
      }),
    );
  }

  get possibleRootcausesWithoutDefaultValue(): RootCauseOption | undefined {
    return this.possibleRootcausesWithoutDefaultOptions.find(
      (option) => option.rootCause === this.args.issue.root_cause,
    );
  }

  @cached
  get possibleStatusOptions(): StatusOption[] {
    return this.args.parentController.possibleStatuses.map((status) => ({
      label: status.name,
      status,
    }));
  }

  get possibleStatusValue(): StatusOption | undefined {
    return this.possibleStatusOptions.find(
      (option) => option.status === this.args.issue.status,
    );
  }

  @action
  handlePossibleAssigneeChange(option: AssigneeOption): void {
    this.args.setIssueProperty('assignee', option.assignee);
  }

  @action
  handlePossibleRootcausesWithoutDefaultChange(option: RootCauseOption): void {
    this.args.setIssueProperty('root_cause', option.rootCause);
  }

  @action
  handlePossibleStatusChange(option: StatusOption): void {
    this.args.setIssueProperty('status', option.status);
  }

  <template>
    <div class="pt-400 pr-500 pb-800 pl-500 {{styles.add-issue-form}}">
      <h2 class="header-title">{{t "parity_manager.add_issue.other_info"}}</h2>
      <div class="mt-400 flex w-full flex-col">
        <label class="input-label">
          {{t "parity_manager.assignee"}}
        </label>
        <PrismComboSelectInput
          @isSearchable={{true}}
          @isSecondary={{true}}
          @onChange={{this.handlePossibleAssigneeChange}}
          @options={{this.possibleAssigneeOptions}}
          @placeholder={{t "general.dropdown.email_search"}}
          @value={{this.possibleAssigneeValue}}
        />
      </div>

      <div class="mt-400 flex w-full flex-col">
        <label class="input-label">
          {{t "parity_manager.issue.action.status"}}
        </label>
        <PrismSelectInput
          @isSecondary={{true}}
          @onChange={{this.handlePossibleStatusChange}}
          @options={{this.possibleStatusOptions}}
          @placeholder={{t "general.dropdown.select_status"}}
          @value={{this.possibleStatusValue}}
        />
      </div>

      <div class="mt-400 flex w-full flex-col">
        <label class="input-label">
          {{t @parentController.rootCauseOrWholesalerTitle}}
        </label>
        <PrismComboSelectInput
          @isSearchable={{true}}
          @isSecondary={{true}}
          @onChange={{this.handlePossibleRootcausesWithoutDefaultChange}}
          @options={{this.possibleRootcausesWithoutDefaultOptions}}
          @placeholder={{t @parentController.rootCauseOrWholesalerPlaceholder}}
          @value={{this.possibleRootcausesWithoutDefaultValue}}
        />
      </div>

      {{#unless @parentController.isBaitAndSwitchView}}
        <div class="mt-400 flex w-full flex-col">
          <PrismInput
            @label={{t "general.rate_code"}}
            @onInput={{fn @setIssuePropertyFromEvent "rate_code"}}
            @value={{@issue.rate_code}}
          />
        </div>
      {{/unless}}

      <div
        class="flex w-full flex-col
          {{if @parentController.isBaitAndSwitchView 'mt-400'}}"
      >
        <label class="input-label">
          {{t "parity_manager.issue.action.comment"}}
        </label>
        <PrismInput
          @onInput={{fn @setIssuePropertyFromEvent "comment"}}
          @placeholder={{t "parity_manager.form.comment"}}
          @removePadding={{true}}
          @rows={{4}}
          @textarea={{true}}
          @value={{@issue.comment}}
        />
      </div>
    </div>

    <div class="settings-modal__footer flex flex-row justify-end">
      <GeneralFormFieldsCancelAndConfirm
        @cancel={{@back}}
        @cancelText={{t "general.back"}}
        @confirm={{@next}}
        @confirmText={{t "parity_manager.attachments.continue"}}
      />
    </div>
  </template>
}
